import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMasterStatusStore } from "../../store/masterStatusStore";
import { useUserStore } from "../../store/userStore";
import type { MasterStatusDto } from "../../models/MasterStatus";
import "animate.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { Spinner, Pagination } from "react-bootstrap";
import { SHOW_MANUALS } from "../../config/features";
import "./ReviewPage.css";

const normalizeKey = (value?: string | null): string =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const normalizeStatusKey = (value?: string | null): string =>
  normalizeKey(value);

function normalizeRoleKey(role?: string | null): string {
  const key = normalizeKey(role);
  if (!key) return "";
  if (key.includes("SUPERADMIN") || key === "ADMIN") return "SUPERADMIN";
  if (key.includes("OEM") && key.includes("REVIEWER")) return "OEMREVIEWER";
  if (key.includes("REVIEWER")) return "REVIEWER";
  if (key.includes("AUTHOR")) return "AUTHOR";
  if (key === "OEM") return "OEM";
  if (key.includes("VENDOR")) return "VENDOR";
  return key;
}

function normalizeRole(u: any): string {
  const raw = typeof u?.role === "string" ? u.role : u?.role?.roleName ?? "";
  return normalizeRoleKey(raw);
}

const TRANSITIONS: Record<number, { next?: number; prev?: number }> = {
  5001: { next: 5002 },
  5002: { next: 5003 },
  5003: { next: 5004 },
  5004: { next: 5006, prev: 5005 },
  5005: { next: 5002 },
  5006: { next: 5007, prev: 5005 },
  5007: { next: 5009, prev: 5008 },
  5008: { next: 5002 },
  5009: { next: 5010, prev: 5008 },
  5010: { next: 5011, prev: 5008 },
  5011: {},
};

const ROLE_EDITABLE_STATUS: Record<string, Set<number>> = {
  AUTHOR: new Set([5001, 5002, 5003, 5004, 5005, 5006]),
  OEM: new Set([5007, 5008, 5009, 5010]),
  REVIEWER: new Set([5003, 5004, 5005, 5006]),
  OEMREVIEWER: new Set([5007, 5008, 5009, 5010]),
};

function canEditCurrentStatus(currentStatusId: number | null | undefined, role: string): boolean {
  if (currentStatusId == null) return false;
  if (role === "SUPERADMIN") return true;
  return ROLE_EDITABLE_STATUS[role]?.has(currentStatusId) ?? false;
}

export default function ReviewPage() {
  const navigate = useNavigate();
  const {
    items,
    statuses,
    loading,
    error,
    hasLoadedOnce,
    load,
    setPartStatus,
    setManualStatus,
  } = useMasterStatusStore();
  const user = useUserStore((s) => s.user);
  const role = normalizeRole(user);

  const [q, setQ] = useState("");
  const [updatingPartRow, setUpdatingPartRow] = useState<number | null>(null);
  const [updatingManualKey, setUpdatingManualKey] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!hasLoadedOnce) void load();
  }, [hasLoadedOnce, load]);

  const statusIdByName = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of statuses) {
      const name = normalizeStatusKey(s.statusType);
      if (name) map.set(name, s.statusId);
    }
    return map;
  }, [statuses]);

  const statusName = (sid?: number | null) =>
    statuses.find((s) => s.statusId === sid)?.statusType ?? (sid ?? "");

  const statusIdFromName = (name?: string | null): number | null => {
    const id = statusIdByName.get(normalizeStatusKey(name));
    return typeof id === "number" ? id : null;
  };

  const partStatusId = (item: MasterStatusDto): number | null => {
    if (typeof item.currentStatusId === "number") return item.currentStatusId;
    return statusIdFromName(item.currentStatus);
  };

  const manualStatusId = (item: MasterStatusDto, manualStatus?: string | null): number | null => {
    return statusIdFromName(manualStatus) ?? partStatusId(item);
  };

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return items;
    return items.filter((it) => {
      const a = (it.componentPartNumber ?? "").toLowerCase();
      const b = (it.componentDescription ?? "").toLowerCase();
      return a.includes(term) || b.includes(term);
    });
  }, [q, items]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, filtered]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const openDetail = (masterAssemblyId?: number | null) => {
    if (!masterAssemblyId) return;
    navigate(`/authoring/view/${masterAssemblyId}`);
  };

  const applyPartStatus = async (workspaceAssemblyId?: number | null, newStatusId?: number) => {
    if (!workspaceAssemblyId || !newStatusId) return;
    try {
      setUpdatingPartRow(workspaceAssemblyId);
      await setPartStatus(workspaceAssemblyId, newStatusId);
    } finally {
      setUpdatingPartRow(null);
    }
  };

  const manualUpdateKey = (masterAssemblyId: number, manualId: number) =>
    `${masterAssemblyId}-${manualId}`;

  const applyManualStatusAction = async (
    item: MasterStatusDto,
    manualId: number,
    newStatusId?: number
  ) => {
    if (!newStatusId) return;
    const key = manualUpdateKey(item.masterAssemblyId, manualId);
    try {
      setUpdatingManualKey(key);
      await setManualStatus(item, manualId, newStatusId);
    } finally {
      setUpdatingManualKey(null);
    }
  };

  const pendingCount = items.filter((it) => partStatusId(it) !== 5011).length;

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const paginationItems = (() => {
    const itemsArr: React.ReactNode[] = [];
    const maxButtons = 7;
    let start = Math.max(0, page - Math.floor(maxButtons / 2));
    let end = start + maxButtons - 1;
    if (end >= totalPages) {
      end = totalPages - 1;
      start = Math.max(0, end - (maxButtons - 1));
    }
    for (let p = start; p <= end; p++) {
      itemsArr.push(
        <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>
          {p + 1}
        </Pagination.Item>
      );
    }
    return itemsArr;
  })();

  const showingFrom = filtered.length === 0 ? 0 : page * rowsPerPage + 1;
  const showingTo = Math.min(filtered.length, (page + 1) * rowsPerPage);
  const reviewGridClassName = SHOW_MANUALS
    ? "review-grid"
    : "review-grid review-grid--catalog-only";

  return (
    <div className="review-page">
      <div className="review-page__topbar">
        <div className="review-page__title-wrap">
          <h2 className="review-page__title">Review Components</h2>
          <span className="review-page__pending">{pendingCount} Pending</span>
        </div>
        <div className="review-page__controls">
          {/* <button
            type="button"
            className="review-btn review-btn--primary"
            onClick={() => navigate("/authoring")}
          >
            <i className="bi bi-pencil-square"></i>
            <span>Authoring</span>
          </button> */}

          <div className="review-search">
            <i className="bi bi-search"></i>
            <input
              type="search"
              placeholder="Search part # or description..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          <button type="button" className="review-btn review-btn--ghost review-btn--status">
            <span>Status: All</span>
            <i className="bi bi-chevron-down"></i>
          </button>

          <button
            type="button"
            className="review-btn review-btn--ghost"
            onClick={() => load()}
            disabled={loading}
          >
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" />
                Refreshing…
              </>
            ) : (
              <>
                <i className="bi bi-arrow-repeat"></i>
                <span>Refresh</span>
              </>
            )}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger review-page__alert">{error}</div>}
      {loading && !hasLoadedOnce && (
        <div className="review-page__loading">
          <Spinner animation="border" />
          <span>Loading components…</span>
        </div>
      )}

      <div className="review-card">
        <div className={`${reviewGridClassName} review-grid--head`}>
          <div>Part Component</div>
          <div>Overall Status</div>
          {SHOW_MANUALS && <div>Associated Manuals</div>}
          <div>Actions</div>
        </div>

        <div className="review-card__body">
          {paginated.map((it) => {
            const sid = partStatusId(it);
            const rowId = it.workspaceAssemblyId ?? it.masterAssemblyId;
            const hasWorkspace = !!it.workspaceAssemblyId;
            const t = sid != null ? TRANSITIONS[sid] : undefined;
            const goNextId = t?.next;
            const goPrevId = t?.prev;
            const canEditHere = canEditCurrentStatus(sid, role) && hasWorkspace;
            const disableNext = !canEditHere || !goNextId || updatingPartRow === rowId;
            const disablePrev = !canEditHere || !goPrevId || updatingPartRow === rowId;

            return (
              <div key={it.masterAssemblyId} className={`${reviewGridClassName} review-grid--row`}>
                <div className="review-part">
                  <div className="review-part__title">
                    <i className="bi bi-box-seam"></i>
                    <span>{it.componentPartNumber ?? "—"}</span>
                  </div>
                  <p className="review-part__desc">{it.componentDescription ?? ""}</p>
                  <p className="review-part__meta">
                    v{it.currentVersionId ?? "-"} • {it.modifiedOn ? new Date(it.modifiedOn).toLocaleString() : "No updates"}
                  </p>
                </div>

                <div className="review-status">
                  <div className="review-status-control">
                    <button
                      type="button"
                      className="review-icon-btn review-icon-btn--reject"
                      disabled={disablePrev}
                      onClick={() => goPrevId && applyPartStatus(it.workspaceAssemblyId, goPrevId)}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>

                    <span className="review-pill">{statusName(sid) || it.currentStatus || "—"}</span>

                    <button
                      type="button"
                      className="review-icon-btn review-icon-btn--next"
                      disabled={disableNext}
                      onClick={() => goNextId && applyPartStatus(it.workspaceAssemblyId, goNextId)}
                    >
                      <i className="bi bi-check-lg"></i>
                    </button>
                  </div>
                </div>

                {SHOW_MANUALS && (
                  <div className="review-manuals">
                    {(it.manuals ?? []).length > 0 ? (
                      (it.manuals ?? []).map((m) => {
                        const msid = manualStatusId(it, m.status);
                        const mt = msid != null ? TRANSITIONS[msid] : undefined;
                        const mNext = mt?.next;
                        const mPrev = mt?.prev;
                        const key = manualUpdateKey(it.masterAssemblyId, m.manualId);
                        const canEditManual = canEditCurrentStatus(msid, role);
                        const disableManualNext =
                          !canEditManual || !mNext || updatingManualKey === key;
                        const disableManualPrev =
                          !canEditManual || !mPrev || updatingManualKey === key;

                        return (
                          <div key={m.manualId} className="review-manual-item">
                            <span className="review-manual-title" title={m.title}>
                              {m.manualType} - {m.title}
                            </span>

                            <div className="review-status-control review-status-control--manual">
                              <button
                                type="button"
                                className="review-icon-btn review-icon-btn--reject"
                                disabled={disableManualPrev}
                                onClick={() =>
                                  mPrev && applyManualStatusAction(it, m.manualId, mPrev)
                                }
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>

                              <span className="review-pill review-pill--manual">
                                {m.status || statusName(msid) || "—"}
                              </span>

                              <button
                                type="button"
                                className="review-icon-btn review-icon-btn--next"
                                disabled={disableManualNext}
                                onClick={() =>
                                  mNext && applyManualStatusAction(it, m.manualId, mNext)
                                }
                              >
                                <i className="bi bi-check-lg"></i>
                              </button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <span className="review-manuals__empty">No manuals</span>
                    )}
                  </div>
                )}

                <div className="review-actions">
                  <button
                    type="button"
                    className="review-view-btn"
                    onClick={() => openDetail(it.masterAssemblyId)}
                  >
                    <i className="bi bi-eye"></i>
                    <span>View Details</span>
                  </button>
                </div>
              </div>
            );
          })}

          {paginated.length === 0 && <div className="review-empty">No components found</div>}
        </div>

        <div className="review-footer">
          <div className="review-footer__count">
            Showing {showingFrom} to {showingTo} of {filtered.length} entries
          </div>

          <div className="review-footer__controls">
            <div className="review-rows">
              <label htmlFor="review-rows-select">Rows per page:</label>
              <select
                id="review-rows-select"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>

            <Pagination className="review-pagination mb-0">
              <Pagination.First onClick={() => setPage(0)} disabled={page === 0} />
              <Pagination.Prev
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0}
              />
              {paginationItems}
              <Pagination.Next
                onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                disabled={page >= totalPages - 1}
              />
              <Pagination.Last
                onClick={() => setPage(totalPages - 1)}
                disabled={page >= totalPages - 1}
              />
            </Pagination>
          </div>
        </div>
      </div>
    </div>
  );
}
